import { useParams } from 'react-router-dom';
import { ConfiguratorGroupDto } from '@/services/configurator';
import { useOptionConfigurator } from '../../../OptionConfiguratorContext';
import { useConfiguratorMutations } from '../../../ConfiguratorMutationContext';
import { groupSelectedOption } from './helpers';

export interface GroupQuantityState {
    /** Render the stepper? False when the group takes no manual quantity, or nothing is picked yet. */
    editable: boolean;
    /** Committed whole-number quantity — the stepper's value. */
    value: number;
    /** Don't accept input right now (a write is in flight, or the group is locked). */
    disabled: boolean;
    /** Persist a new quantity by re-sending the group's current pick. No-op without a selection. */
    commit: (quantity: number) => void;
}

/**
 * Group-level quantity editing for the immersive option item menu — the counterpart to the
 * per-option quantity fields on `useOptionSelection` (which drive the standard UI's per-card
 * stepper). `allowManualQuantity` and `quantity` live on the group, so the immersive menu shows one
 * stepper per group rather than one per option card.
 */
export const useGroupQuantity = (group: ConfiguratorGroupDto): GroupQuantityState => {
    const { viewOnly, isShowroom } = useOptionConfigurator();
    const { selectOption, isSelecting } = useConfiguratorMutations();
    const { roomId } = useParams<{ roomId: string }>();

    const selected = groupSelectedOption(group);

    return {
        // A quantity is persisted as part of a selection (the authenticated SetSelectionRequest and
        // the showroom's stored ShowroomOptionSelection both carry it alongside the option id), so
        // there is nothing to commit against until the buyer has picked an option.
        editable: group.allowManualQuantity && !viewOnly && !!selected,
        // Manual entry is whole-number, so round the committed value for the prefill.
        value: group.quantity != null ? Math.max(1, Math.round(group.quantity)) : 1,
        disabled: isSelecting || (group.isLocked && !isShowroom),
        commit: (quantity: number) => {
            if (!selected) return;
            // Pass the active room explicitly and select against the group's OWN deadline — the URL
            // deadline is vestigial in the multi-deadline preview (mirrors handleClear in
            // GroupOptionDetail).
            selectOption({
                option: selected,
                customFieldData: selected.customFieldData ?? null,
                roomId: roomId && roomId !== '0' ? Number(roomId) : null,
                deadlineId: group.deadlineId,
                quantity,
            });
        },
    };
};
